import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { notes as api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, StickyNote, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function NotesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");

  async function load() {
    setLoading(true);
    try { setList(await api.list()); } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() {
    setActive({ id: null });
    setTitle("");
    setBody("");
    setTags("");
  }

  function openNote(n) {
    setActive(n);
    setTitle(n.title);
    setBody(n.body || "");
    setTags((n.tags || []).join(", "));
  }

  async function save() {
    if (!title.trim()) return toast.error("Title required");
    const payload = { title, body, tags: tags.split(",").map(t => t.trim()).filter(Boolean) };
    try {
      if (active?.id) await api.update(active.id, payload);
      else await api.create(payload);
      toast.success("Saved");
      setActive(null);
      load();
    } catch { toast.error("Save failed"); }
  }

  async function remove(id) {
    if (!window.confirm("Delete note?")) return;
    try { await api.remove(id); load(); setActive(null); } catch { toast.error("Delete failed"); }
  }

  return (
    <div className="animate-fade-up">
      <div className="mb-8 flex items-end justify-between gap-3">
        <div>
          <span className="label-caps">Utility</span>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Notes.</h1>
          <p className="mt-2 text-[color:var(--text-muted)] max-w-xl">Quick thoughts, tagged and searchable. Saved to this device's profile.</p>
        </div>
        <Button onClick={openNew} data-testid="note-new" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white h-11"><Plus className="h-4 w-4 mr-2" /> New note</Button>
      </div>

      {loading ? <div className="text-[color:var(--text-muted)]">Loading…</div> :
        list.length === 0 && !active ? (
          <div className="card-tactile rounded-2xl p-10 flex flex-col items-center text-center" data-testid="empty-notes">
            <div className="h-16 w-16 rounded-full bg-[color:var(--primary-soft)] flex items-center justify-center mb-4"><StickyNote className="h-7 w-7 text-[color:var(--primary-ink)]" /></div>
            <h3 className="font-display font-bold text-xl">No notes yet</h3>
            <p className="text-sm text-[color:var(--text-muted)] mt-1">Capture your first thought.</p>
            <Button onClick={openNew} data-testid="empty-new-note" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white mt-4"><Plus className="h-4 w-4 mr-2" /> New note</Button>
          </div>
        ) : active ? (
          <div className="card-tactile rounded-2xl p-6 max-w-3xl">
            <button onClick={() => setActive(null)} className="inline-flex items-center gap-2 text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] mb-4" data-testid="note-back"><ArrowLeft className="h-4 w-4" /> All notes</button>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="note-title" className="text-xl font-display font-bold" />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write…" data-testid="note-body" className="mt-3 min-h-[220px]" />
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tags, comma separated" data-testid="note-tags" className="mt-3" />
            <div className="flex justify-between items-center mt-4">
              {active.id ? (
                <button onClick={() => remove(active.id)} className="text-sm text-[color:var(--accent-ink)] hover:underline inline-flex items-center gap-1" data-testid="note-delete">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              ) : <span />}
              <Button onClick={save} data-testid="note-save" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white">Save</Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((n, i) => (
              <button key={n.id} onClick={() => openNote(n)} data-testid={`note-open-${n.id}`} className="card-tactile rounded-2xl p-5 text-left animate-fade-up" style={{ animationDelay: `${i * 30}ms` }}>
                <h3 className="font-display font-bold text-lg tracking-tight line-clamp-1">{n.title}</h3>
                <p className="text-sm text-[color:var(--text-muted)] mt-1 line-clamp-4 whitespace-pre-wrap">{n.body || <em>Empty</em>}</p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(n.tags || []).map(t => <span key={t} className="dose-pill pending">{t}</span>)}
                </div>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}
